import {
  Component,
  Event,
  EventEmitter,
  Host,
  Listen,
  Prop,
  State,
  h,
} from '@stencil/core';
import { Unsubscribe, store } from '@stencil/redux';
import {
  RandomReaderChallenge,
  RandomReaderResponseInfo,
} from '../../class/class';
import { CreditCardType, Language, SourceName } from '../../enums/const-enums';
import { uiReady } from '../../redux/actions/config-actions';
import { clearNotification } from '../../redux/actions/notification-action';
import { RootState, initialStore } from '../../redux/store';
import { GetChallenge } from '../../service/get-challenge';
import { SignSubmit } from '../../service/sign-submit';
import {
  getActionType,
  handleErrorResponse,
  isLogin,
  removeSpaces,
} from '../../utils/utils';
import {
  RassRandomReaderTranslations,
  rassRandomReaderTranslations,
} from './i18n/rass-random-reader.i18n';

@Component({
  tag: 'rass-random-reader',
  styleUrl: 'rass-random-reader.scss',
  shadow: false,
})
export class RassRandomReader {
  @State() assetsPath: string;
  @State() language: Language;
  @State() sourceName: SourceName;
  @State() challengeData: RandomReaderChallenge;
  @State() accountNumber: string;
  @State() cardNumber: string;
  @State() isCardValid: boolean;
  @State() creditCardType: CreditCardType;
  @State() creditCardSuffix: string;
  @State() isChallengeRequestPending = false;
  @State() isSubmitRequestPending = false;
  @Prop({ mutable: true, reflect: true }) hasErrorStatus: string = null;
  @Event({ eventName: 'code:reset' }) resetCodeEvent: EventEmitter;
  clearNotification!: typeof clearNotification;
  uiReady!: typeof uiReady;
  unsubscribe!: Unsubscribe;

  componentWillLoad(): void {
    store.setStore(initialStore);
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      const {
        config: { assetsPath, language, sourceName },
        card: {
          accountNumber,
          cardNumber,
          creditCardType,
          creditCardSuffix,
          isCardValid,
        },
        challengeData,
      } = state;
      return {
        assetsPath,
        language,
        sourceName,
        accountNumber,
        cardNumber,
        creditCardType,
        creditCardSuffix,
        isCardValid,
        challengeData,
      };
    });
    store.mapDispatchToProps(this, {
      clearNotification,
      uiReady,
    });
    this.clearNotification();
  }

  componentDidLoad(): void {
    window.scrollTo(0, 0);
    (document.activeElement as HTMLElement).blur();
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Tab' }));
    this.uiReady();
  }

  @Listen('rass:card:complete')
  getChallenge(): void {
    if (!isLogin()) {
      this.isChallengeRequestPending = true;
      new GetChallenge()
        .doGetChallenge()
        .then(() => {
          this.isChallengeRequestPending = false;
        })
        .catch((error: AppModel.HttpResponseErrorJson) => {
          handleErrorResponse(error, this.translations.errors.timeout);
          this.isChallengeRequestPending = false;
        });
    }
  }

  @Listen('rass:code:submit')
  onCodeSubmit(event: CustomEvent<string>): void {
    this.isSubmitRequestPending = true;
    this.clearNotification();
    const responseInfo = new RandomReaderResponseInfo(
      event.detail,
      this.creditCardType === null ? removeSpaces(this.accountNumber) : null,
      this.creditCardType === null ? this.cardNumber : null,
    );
    new SignSubmit()
      .doSignSubmit(responseInfo)
      .catch((error: AppModel.HttpResponseErrorJson) => {
        handleErrorResponse(error, this.translations.errors.timeout);
        this.isSubmitRequestPending = false;
        this.resetCodeEvent.emit();
        this.getChallenge();
      });
  }

  // Split the digits of the number for the screen reader to read them individually
  splitDigits(value: string): string {
    return value
      .split('')
      .map(
        (char) =>
          `<span 
            class="voice-rate-slow digits" 
            aria-label="${this.translations.numberMap[char]}"
          >${char}</span>`,
      )
      .join('');
  }

  getChallengeInstructions(): string {
    const segments = this.translations.instructions.instructionSegments;

    const result = this.challengeData.challenges
      .map(
        (challenge) => `
        <li>
          ${segments.enterChecksum.leftSide[challenge.key]} 
          <strong>${this.splitDigits(challenge.value)}</strong>
          <span>${segments.enterChecksum.rightSide[challenge.key]}</span>
        </li>`,
      )
      .join('');

    return `${result}<li>${segments.okAgain}</li>`;
  }

  formatInstructions(): string {
    const segments = this.translations.instructions.instructionSegments;

    return `
      <ol class="sfc-ordered-list">
        <li id="first-instruction">
          ${this.cardInstruction()}
        </li>
        <li>${segments.pressButton[getActionType()]}</li>
        <li>${segments.enterPIN}</li>
        ${isLogin() ? '' : this.getChallengeInstructions()}
        <li>${segments.enterCode[getActionType()]}</li>
      </ol>
    `;
  }

  cardInstruction(): string {
    const translations =
      this.translations.instructions.instructionSegments.card;

    return this.creditCardType === null
      ? `${translations.leftSide.debitCard}<strong>${this.splitDigits(this.cardNumber)}</strong>${translations.rightSide}`
      : `${translations.leftSide.creditCard}<strong role="text">************${this.splitDigits(this.creditCardSuffix)}</strong>${translations.rightSide}`;
  }

  get challengeInstructions(): string {
    if (this.isCardValid && (this.challengeData.challenges || isLogin())) {
      return this.formatInstructions();
    }
    return this.translations.instructions.instructionSegments.initial;
  }

  get translations(): RassRandomReaderTranslations {
    return (
      rassRandomReaderTranslations[this.language] ||
      rassRandomReaderTranslations[Language.EN]
    );
  }

  render(): void {
    return (
      <Host class="section-random-reader">
        <style>@import url({`'${this.assetsPath}css/sfc-style.css'`});</style>
        <div class="sfc-container body-width" role="form">
          <rass-card-input
            is-disabled={
              this.isChallengeRequestPending || this.isSubmitRequestPending
            }
          />

          <div class="sfc-row sfc-justify-content-center sfc-margin-block-end-150">
            <div class="sfc-col-12">
              <div class="sfc-row">
                <div class="sfc-col-xs-12 sfc-col-sm-9">
                  <p
                    id="challenge-instructions"
                    class="random-reader-instructions sfc-paragraph sfc-font-text-align-start sfc-text--size-md"
                    aria-live="polite"
                    innerHTML={this.challengeInstructions}
                  />
                </div>
                <div class="sfc-display-none sfc-display-sm-block sfc-col-xs-12 sfc-col-sm-3">
                  <img
                    class="random-reader-image"
                    src={`${this.assetsPath}images/random_reader_comfort.png`}
                    alt="Random Reader Comfort"
                  />
                </div>
              </div>
            </div>
          </div>

          <rass-code-submit
            is-disabled={!this.isCardValid || this.isSubmitRequestPending}
          />
        </div>
      </Host>
    ) as void;
  }
}
